import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';

import { gameApiContext } from '../../context';
import type { GameAPIType } from '../../GameAPI';
import { tokens } from '../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../UIState';
import { bindValtio } from '../../ValtioController';

/**
 * Context Menu - Player interaction popup
 *
 * Shows a context menu for interacting with other players.
 * Appears when clicking on a player in the scoreboard or chat.
 *
 * Features:
 * - Whisper (private message)
 * - Ignore/Unignore
 * - Vote mute
 * - Click outside to close
 *
 * @element context-menu
 */
@customElement('context-menu')
export class ContextMenu extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        pointer-events: auto;
        /* Declared here rather than inherited from the body — the component
           owns its own typography, and a menu you click shouldn't select its
           own labels under a slow tap. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
      }

      .menu {
        position: fixed;
        width: 160px;
        /* A floating menu has its own look, distinct from the panel tokens: a
           slightly-lifted near-black fill, a faint light edge, and a heavier
           drop shadow + 1px ring. Intentionally bespoke (no token match). */
        background-color: rgba(20, 20, 20, 0.98);
        border-radius: var(--radius-lg);
        border: 1px solid var(--color-overlay-light);
        padding: 10px;
        font-size: var(--font-size-13);
        box-shadow:
          0 8px 30px 0 rgba(0, 0, 0, 0.7),
          0 0 0 1px rgba(0, 0, 0, 0.5);
        /* Above panels (300) in the app z ladder — see .panel-overlay. */
        z-index: 400;
      }

      .header {
        cursor: default;
        font-weight: bold;
        color: var(--color-text-white);
        padding: 8px 10px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: var(--font-size-lg);
      }

      .item {
        color: var(--color-text-tooltip);
        cursor: pointer;
        padding: 8px 10px;
        border-radius: var(--radius-md);
        /* Reachable on touch via the scoreboard — crisp taps, no grey flash. */
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      /* Hover-only — gated so a tap on touch doesn't flash the hover style.
         (The menu closes on tap, so this is mainly the flash + consistency.) */
      @media (hover: hover) {
        .item:hover {
          background-color: var(--color-overlay-light);
          color: var(--color-text-white);
        }
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  /** Menu element for viewport clamp */
  @query('.menu') private menuEl?: HTMLElement;

  /** Pending timer for the deferred outside-click listener add. Tracked so a
   *  disconnect before it fires can cancel it — otherwise the timer would add a
   *  window listener that nothing ever removes. */
  private openClickTimer: number | null = null;

  /** @override Registers window listeners to close menu */
  override connectedCallback() {
    super.connectedCallback();
    // Defer the outside-click listener by a tick: this element is conditionally
    // mounted, so it connects while the click that opened the menu is still in
    // flight — adding the listener now would let that click immediately close it.
    this.openClickTimer = window.setTimeout(() => {
      this.openClickTimer = null;
      window.addEventListener('click', this.handleWindowClick);
    }, 0);
    // Use capture phase so ESC is handled before score-panel's document listener
    window.addEventListener('keydown', this.handleKeyDown, true);
  }

  /** Inputs the clamp last ran against, so an unrelated re-render doesn't pay
   *  for a fresh getBoundingClientRect. The component subscribes to the whole
   *  uiState proxy, so it re-renders on every leaderboard/ping/stats tick while
   *  the menu is open, and each of those would otherwise force a layout. */
  private lastClamp = '';

  /** @override Clamps menu within viewport after each render */
  override updated() {
    const el = this.menuEl;
    if (!el) return;
    const menu = this.uiState.contextMenu;
    if (!menu) return;

    // uiState.viewport is the visually-visible area (Responsive.ts measures
    // visualViewport). window.innerHeight is the *layout* viewport, which on
    // iOS stays tall while the URL bar covers the bottom of the screen -
    // clamping against it parks the menu under the browser chrome. Reading it
    // from state also makes the clamp re-run on viewport changes for free,
    // since that write re-renders us.
    // Key on everything that can move or resize the box: the requested
    // position, the viewport, and the two flags that change an item's label
    // (and so its height, if a label wraps to a second line).
    const { width: vw, height: vh } = this.uiState.viewport;
    const isTracked = this.uiState.trackedPlayerId === menu.playerId;
    const key = `${menu.top},${menu.left},${vw},${vh},${menu.isIgnored},${isTracked}`;
    if (key === this.lastClamp) return;
    this.lastClamp = key;

    const MARGIN = 8;
    const rect = el.getBoundingClientRect();
    const maxLeft = vw - rect.width - MARGIN;
    const maxTop = vh - rect.height - MARGIN;
    // Clamp to [MARGIN, max] on both axes so a too-small / off-screen producer
    // coordinate can't park the menu partly outside the viewport.
    const left = Math.max(MARGIN, Math.min(menu.left, maxLeft));
    const top = Math.max(MARGIN, Math.min(menu.top, maxTop));
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }

  /** @override Cleans up window listeners */
  override disconnectedCallback() {
    super.disconnectedCallback();
    // Cancel a not-yet-fired listener add so it can't orphan a window listener.
    if (this.openClickTimer !== null) {
      window.clearTimeout(this.openClickTimer);
      this.openClickTimer = null;
    }
    window.removeEventListener('click', this.handleWindowClick);
    window.removeEventListener('keydown', this.handleKeyDown, true);
  }

  /** Handles window click to close menu when clicking outside */
  private handleWindowClick = () => {
    this.api.closeContextMenu();
  };

  /** Handles ESC key to close menu */
  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      this.api.closeContextMenu();
    }
  };

  /** Every item does the same thing: click, act on the menu's player, then
   *  close. No stopPropagation needed here — the .menu container already stops
   *  clicks from inside reaching the window listener that closes on outside
   *  clicks. The ESC and outside-click closes stay silent (the click rule).
   *
   *  `toggleTrackedPlayer` is single-target: selecting a different player while
   *  one is already tracked switches the track rather than adding a second. */
  private act(action: (playerId: number) => void) {
    return () => {
      this.api.playClick();
      const menu = this.uiState.contextMenu;
      if (menu) {
        action(menu.playerId);
      }
      this.api.closeContextMenu();
    };
  }

  /** Keeps a click inside the menu from reaching the window listener that
   *  closes it. One handler on the container covers every child. */
  private stopClick = (e: Event) => {
    e.stopPropagation();
  };

  // Built once, not per render: Lit tears down and re-adds a listener whenever
  // its identity changes, and this component re-renders on unrelated state.
  private handleWhisper = this.act((id) => this.api.whisperPlayer(id));
  private handleIgnore = this.act((id) => this.api.toggleIgnorePlayer(id));
  private handleVotemute = this.act((id) => this.api.votemutePlayer(id));
  private handleTrack = this.act((id) => this.api.toggleTrackedPlayer(id));

  /** @override Renders the context menu */
  override render() {
    const menu = this.uiState.contextMenu;
    if (!menu) return nothing;

    const ignoreText = menu.isIgnored ? 'Unignore' : 'Ignore';
    const isTracked = this.uiState.trackedPlayerId === menu.playerId;
    // "Stop tracking on minimap" wrapped to a second line in the 160px menu,
    // so the menu grew by a row whenever a player was tracked.
    const trackText = isTracked ? 'Stop tracking' : 'Track on minimap';

    return html`
      <div class="menu" style="top: ${menu.top}px; left: ${menu.left}px;" @click=${this.stopClick}>
        <div class="header">${menu.playerName}</div>
        <div class="item" @click=${this.handleWhisper}>Whisper</div>
        <div class="item" @click=${this.handleIgnore}>${ignoreText}</div>
        <div class="item" @click=${this.handleVotemute}>Vote mute</div>
        <div class="item" @click=${this.handleTrack}>${trackText}</div>
      </div>
    `;
  }
}
